import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Building2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  connectBankSchema,
  type ConnectBankForm,
} from "@/schemas/tpaygate.schema";
import { useSupportedBanks, useConnectBank } from "@/hooks/useTPaygateQueries";
import { adminTPaygateService } from "@/services/admin-tpaygate.service";
import { OtpConfirmDialog } from "./OtpConfirmDialog";
import type { ConnectBankResult } from "@/types/tpaygate";

interface Props {
  open: boolean;
  onClose: () => void;
  onConnected: () => void;
}

type ConnectMethod = "embedded" | "api";

export function ConnectBankDialog({ open, onClose, onConnected }: Props) {
  const [method, setMethod] = useState<ConnectMethod>("embedded");
  const [embeddedBankCode, setEmbeddedBankCode] = useState<string>("");
  const [embeddedLoading, setEmbeddedLoading] = useState(false);

  const [pendingConnect, setPendingConnect] =
    useState<ConnectBankResult | null>(null);
  const [otpOpen, setOtpOpen] = useState(false);

  const popupRef = useRef<Window | null>(null);
  const allowedOriginRef = useRef<string | null>(null);
  const onConnectedRef = useRef(onConnected);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onConnectedRef.current = onConnected;
  }, [onConnected]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const { data: banks = [], isLoading: banksLoading } = useSupportedBanks();
  const connectBank = useConnectBank();

  const form = useForm<ConnectBankForm>({
    resolver: zodResolver(connectBankSchema),
    defaultValues: {
      bankCode: "",
      merchantName: "",
      accountName: "",
      accountNo: "",
      identity: "",
      phone: "",
      email: "",
      prefix: "",
      clientId: "",
      encryptKey: "",
      secretKey: "",
    },
  });

  // Listen for postMessage from T-PayGate embedded UI popup — uses refs to avoid stale closures
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (allowedOriginRef.current && event.origin !== allowedOriginRef.current)
        return;
      if (event.data?.type === "tabClosed" || event.data === "tabClosed") {
        popupRef.current?.close();
        popupRef.current = null;
        onConnectedRef.current();
        onCloseRef.current();
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const handleClose = () => {
    form.reset();
    setEmbeddedBankCode("");
    setMethod("embedded");
    setPendingConnect(null);
    onClose();
  };

  const guard = useCloseGuard({
    open,
    onOpenChange: (v) => !v && handleClose(),
    isDirty:
      method === "api"
        ? form.formState.isDirty
        : embeddedBankCode.trim().length > 0,
    isSubmitting: method === "api" ? connectBank.isPending : embeddedLoading,
    onDiscard: () => {
      form.reset();
      setEmbeddedBankCode("");
    },
  });

  // ── Embedded UI (Method A) ─────────────────────────────────────────────────

  const handleEmbeddedConnect = async () => {
    if (!embeddedBankCode) return;
    setEmbeddedLoading(true);
    try {
      const { connectUrl } =
        await adminTPaygateService.getConnectUrl(embeddedBankCode);
      try {
        allowedOriginRef.current = new URL(connectUrl).origin;
      } catch {
        allowedOriginRef.current = null;
      }
      const popup = window.open(
        connectUrl,
        "tpaygate-connect",
        "width=640,height=720,left=200,top=100,resizable=yes,scrollbars=yes",
      );
      if (!popup) {
        window.open(connectUrl, "_blank");
        toast.info("Popup blocked", {
          description: "The connect form has been opened in a new tab.",
        });
      } else {
        popupRef.current = popup;
      }
    } catch (err) {
      // error already shown by service
      console.error(err);
    } finally {
      setEmbeddedLoading(false);
    }
  };

  // ── API (Method B) ────────────────────────────────────────────────────────

  const handleApiConnect = async (values: ConnectBankForm) => {
    const result = await connectBank.mutateAsync(values);
    if (result.isOTPConfirmation) {
      setPendingConnect(result);
      setOtpOpen(true);
    } else {
      // Already connected without OTP
      onConnected();
      handleClose();
    }
  };

  const handleOtpSuccess = () => {
    setOtpOpen(false);
    setPendingConnect(null);
    onConnected();
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Connect Bank Account</DialogTitle>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <DialogDescription>
              Link a bank account to T-PayGate. Use the{" "}
              <strong>Embedded UI</strong> (recommended) to let T-PayGate handle
              all bank-specific fields, or use the <strong>API form</strong> for
              manual entry.
            </DialogDescription>
            <Tabs
              value={method}
              onValueChange={(v) => setMethod(v as ConnectMethod)}
            >
              <TabsList className="w-full">
                <TabsTrigger value="embedded" className="flex-1">
                  Embedded UI
                  <Badge variant="secondary" className="ml-2 text-xs">
                    Recommended
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="api" className="flex-1">
                  API Form
                </TabsTrigger>
              </TabsList>

              {/* ── Method A: Embedded UI ── */}
              <TabsContent value="embedded" className="space-y-4 pt-2">
                <p className="text-sm text-muted-foreground">
                  Select a bank and T-PayGate will open a secure hosted form in
                  a popup window. Complete the steps there — OTP, account
                  verification — and this page refreshes automatically when
                  done.
                </p>

                <div className="space-y-2">
                  <Label>Bank</Label>
                  {banksLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading
                      banks…
                    </div>
                  ) : (
                    <Select
                      value={embeddedBankCode}
                      onValueChange={setEmbeddedBankCode}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a bank…" />
                      </SelectTrigger>
                      <SelectContent>
                        {banks.map((b) => (
                          <SelectItem key={b.code} value={b.code}>
                            <div className="flex items-center gap-2">
                              {b.logoUrl ? (
                                <img
                                  src={b.logoUrl}
                                  alt={b.code}
                                  className="h-4 w-4 object-contain"
                                />
                              ) : (
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                              )}
                              {b.shortName ?? b.name}
                              <span className="text-xs text-muted-foreground">
                                ({b.code})
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </TabsContent>

              {/* ── Method B: API Form ── */}
              <TabsContent value="api" className="pt-2">
                <Form {...form}>
                  <form
                    id="connect-bank-form"
                    onSubmit={form.handleSubmit(handleApiConnect)}
                    className="space-y-3"
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <FormField
                        control={form.control}
                        name="bankCode"
                        render={({ field }) => (
                          <FormItem className="col-span-2">
                            <FormLabel>
                              Bank{" "}
                              <span className="text-destructive-ink">*</span>
                            </FormLabel>
                            <FormControl>
                              {banksLoading ? (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground h-9">
                                  <Loader2 className="h-4 w-4 animate-spin" />{" "}
                                  Loading…
                                </div>
                              ) : (
                                <Select
                                  value={field.value}
                                  onValueChange={field.onChange}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a bank…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {banks.map((b) => (
                                      <SelectItem key={b.code} value={b.code}>
                                        {b.shortName ?? b.name} ({b.code})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="merchantName"
                        render={({ field }) => (
                          <FormItem className="col-span-2">
                            <FormLabel>
                              Merchant Name{" "}
                              <span className="text-destructive-ink">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="My Store" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="accountName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Account Name{" "}
                              <span className="text-destructive-ink">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="NGUYEN VAN A" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="accountNo"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Account Number{" "}
                              <span className="text-destructive-ink">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="108800888060" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="0987654321" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                type="email"
                                placeholder="shop@example.com"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="identity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Identity (CCCD)</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="012345678901" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="prefix"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Merchant Prefix</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="merchant_xxx" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="clientId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Bank Client ID</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="encryptKey"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Encrypt Key</FormLabel>
                            <FormControl>
                              <Input {...field} type="password" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="secretKey"
                        render={({ field }) => (
                          <FormItem className="col-span-2">
                            <FormLabel>Secret Key</FormLabel>
                            <FormControl>
                              <Input {...field} type="password" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <p className="text-xs text-muted-foreground">
                      Required fields vary by bank. Leave optional fields blank
                      if not applicable.
                    </p>
                  </form>
                </Form>
              </TabsContent>
            </Tabs>
          </DialogBody>
          {/* One footer for both tabs: the primary button is the open tab's own action, and the
              API tab's submit reaches its form through `form=`. */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
            >
              Cancel
            </Button>
            {method === "embedded" ? (
              <Button
                type="button"
                onClick={handleEmbeddedConnect}
                disabled={!embeddedBankCode || embeddedLoading}
              >
                {embeddedLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="mr-2 h-4 w-4" />
                )}
                Open Connect Form
              </Button>
            ) : (
              <Button
                type="submit"
                form="connect-bank-form"
                disabled={connectBank.isPending}
              >
                {connectBank.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                    Connecting…
                  </>
                ) : (
                  "Connect Bank"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.dialog}

      {pendingConnect && (
        <OtpConfirmDialog
          configBankId={pendingConnect.configBankId}
          open={otpOpen}
          onClose={() => setOtpOpen(false)}
          onSuccess={handleOtpSuccess}
        />
      )}
    </>
  );
}
